import { Close } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import { Input } from '@/components/Input';
import { useYaMetrika } from '@/entities/yaMetrika';
import { colorStore } from '@/features/queueColors';
import { AnalyticsEvent } from '@/types/yametrika';
import { hslToHex } from '@/utils/colorConversions';

import { Button } from '../../components/Button';

import { useHexInput } from './hooks/useHexInput';
import { ColorPreview } from './ui/ColorPreview';

import s from './ChangeQueueColorModal.module.scss';

type ChangeQueueColorModalProps = {
  open: boolean;
  onClose: () => void;
  queue: string;
};

export const ChangeQueueColorModal: React.FC<ChangeQueueColorModalProps> = observer(
  ({ open, onClose, queue }) => {
    const currentColor = colorStore.getColorByPrefix(queue);
    const [selectedColor, setSelectedColor] = useState(currentColor);
    const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);
    const { sendEvent } = useYaMetrika();

    const hexInput = useHexInput(setSelectedColor);

    useEffect(() => {
      setSelectedColor(currentColor);
      hexInput.reset();
      setColorPickerAnchor(null);
    }, [currentColor]);

    const handleColorPickerChange = (color: string) => {
      setSelectedColor(color);
      hexInput.reset();
    };

    const handleHexValueClick = () => {
      hexInput.setColorInput(hslToHex(selectedColor));
      hexInput.setIsHexInputVisible(true);
    };

    const handleSave = () => {
      colorStore.updateQueueColor(queue, selectedColor);
      sendEvent(AnalyticsEvent.changeLogColor);
      onClose();
    };

    const handleCancel = () => {
      setSelectedColor(currentColor);
      onClose();
    };

    const handleReset = () => {
      colorStore.resetQueueColor(queue);
      onClose();
    };

    return (
      <Dialog
        open={open}
        onClose={handleCancel}
        fullWidth
        maxWidth="sm"
        sx={{
          '.MuiPaper-root': {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <h2 className={s.modal__title}>Изменить цвет очереди</h2>
          <IconButton onClick={handleCancel} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <div className={s.modal__content}>
            <div className={s.modal__queue_name}>
              <span className={s.modal__label}>Очередь:</span>
              <span className={s.modal__queue_value} style={{ color: currentColor }}>
                {queue}
              </span>
            </div>

            <div className={s.modal__color_section}>
              <div className={s.modal__color_row}>
                <ColorPreview label="Текущий:" color={currentColor} isHexInputVisible={false} />

                <ColorPreview
                  label="Новый:"
                  color={selectedColor}
                  isEditable
                  onColorChange={handleColorPickerChange}
                  onColorPickerOpen={setColorPickerAnchor}
                  colorPickerAnchor={colorPickerAnchor}
                  onColorPickerClose={() => setColorPickerAnchor(null)}
                  isHexInputVisible={hexInput.isHexInputVisible}
                  onHexValueClick={handleHexValueClick}
                  hexInput={
                    <Input
                      value={hexInput.colorInput}
                      onChange={(e) => hexInput.handleHexInputChange(e.target.value)}
                      onBlur={hexInput.handleHexInputBlur}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (hexInput.handleHexInputKeyDown(e)) {
                          e.preventDefault();
                        }
                      }}
                      placeholder="#ff0000"
                      autoFocus
                      error={!hexInput.isValid}
                      sx={{
                        '& .MuiInputBase-root': {
                          height: '32px',
                          fontSize: '14px',
                        },
                      }}
                    />
                  }
                />
              </div>
            </div>
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleReset} variant="outlined" sx={{ borderRadius: '8px' }}>
            Сбросить
          </Button>
          <Button onClick={handleCancel} variant="outlined" sx={{ borderRadius: '8px' }}>
            Отменить
          </Button>
          <Button onClick={handleSave} variant="contained" sx={{ borderRadius: '8px' }}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);
